// The idle prefetch behind the ≤ 300 ms period switch (REQ-280 AC2; ARCH §9.3).
//
// While the user reads one period, the presets either side of it in the date control are fetched into the
// cache during idle time, so the next click is served from cache and `keepPreviousData` covers the rest.
//
//   useEffect(() => {
//     const id = requestIdleCallback(() => prefetchNeighbouringPeriods(queryClient, queries, accountId, view, today));
//     return () => cancelIdleCallback(id);
//   }, [accountId, view.preset, view.compare, today]);

import type { QueryClient } from '@tanstack/query-core';
import type { PeriodQuery } from '../api/ApiClient';
import type { IsoDate } from '../types/primitives';
import type { PeriodPreset } from '../types/settings';
import { resolvePeriod } from '../periods/periods';
import { PERIOD_PRESET_ORDER } from '../viewState/viewState';
import type { Queries } from './queries';

/** The presets either side of `preset` in the date control (REQ-021 AC1 order), nearest first; `custom` has
 *  no dates of its own and is never a neighbour (nor has any). */
export function neighbouringPresets(preset: PeriodPreset): PeriodPreset[] {
  if (preset === 'custom') return [];
  const order = PERIOD_PRESET_ORDER.filter((p) => p !== 'custom');
  const i = order.indexOf(preset);
  if (i < 0) return [];
  const out: PeriodPreset[] = [];
  const next = order[i + 1];
  const prev = order[i - 1];
  // the next preset is the likelier click from the default (Last 7 days → Last 30 days), so it goes first
  if (next !== undefined) out.push(next);
  if (prev !== undefined) out.push(prev);
  return out;
}

/** `PeriodQuery`s for the neighbouring presets against the account's `today`, with the view's compare toggle. */
export function neighbouringPeriodQueries(
  preset: PeriodPreset,
  today: IsoDate,
  compare: boolean,
): PeriodQuery[] {
  return neighbouringPresets(preset).map((p) => {
    const r = resolvePeriod(p, today);
    return { since: r.since, until: r.until, compare: compare ? 'previous' : 'none' };
  });
}

/** Warm the cache with Home's two reads for each neighbouring preset. Errors are swallowed by
 *  `prefetchQuery` itself (a prefetch never surfaces); already-fresh entries are not refetched. */
export function prefetchNeighbouringPeriods(
  queryClient: Pick<QueryClient, 'prefetchQuery'>,
  queries: Pick<Queries, 'summary' | 'timeseries'>,
  accountId: string,
  view: { preset: PeriodPreset; compare: boolean },
  today: IsoDate,
): Promise<void> {
  const work: Promise<void>[] = [];
  for (const q of neighbouringPeriodQueries(view.preset, today, view.compare)) {
    work.push(queryClient.prefetchQuery(queries.summary(accountId, q)));
    work.push(queryClient.prefetchQuery(queries.timeseries(accountId, q)));
  }
  return Promise.all(work).then(() => undefined);
}
